import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowUp, ArrowDown, Equals, Clock, ArrowsClockwise, ChartLineUp, ShoppingCart, Lightning, House as HouseIcon, Car, FirstAid, TShirt, CarProfile
} from "@phosphor-icons/react";
import CpiChart from "@/components/CpiChart";

const CATEGORY_CONFIG = {
  all_items: { icon: ChartLineUp, color: "#3b82f6", label: "All Items CPI", accent: "blue" },
  food: { icon: ShoppingCart, color: "#f59e0b", label: "Food", accent: "amber" },
  energy: { icon: Lightning, color: "#ef4444", label: "Energy", accent: "red" },
  core: { icon: ChartLineUp, color: "#8b5cf6", label: "Core (Less Food & Energy)", accent: "violet" },
  shelter: { icon: HouseIcon, color: "#06b6d4", label: "Shelter", accent: "cyan" },
  transportation: { icon: Car, color: "#f97316", label: "Transportation", accent: "orange" },
  medical: { icon: FirstAid, color: "#ec4899", label: "Medical Care", accent: "pink" },
  apparel: { icon: TShirt, color: "#14b8a6", label: "Apparel", accent: "teal" },
  new_vehicles: { icon: CarProfile, color: "#6366f1", label: "New Vehicles", accent: "indigo" },
  used_vehicles: { icon: CarProfile, color: "#a855f7", label: "Used Vehicles", accent: "purple" },
};

function ChangeIndicator({ value, suffix = "%" }) {
  if (value === null || value === undefined) return <span className="text-zinc-600 text-xs font-data">--</span>;
  const isPositive = value > 0;
  const isZero = value === 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-data font-medium ${isZero ? "text-zinc-400" : isPositive ? "text-red-400" : "text-emerald-400"}`}>
      {isPositive ? <ArrowUp size={10} weight="bold" /> : isZero ? <Equals size={10} /> : <ArrowDown size={10} weight="bold" />}
      {isPositive ? "+" : ""}{value.toFixed(2)}{suffix}
    </span>
  );
}

function HeroCard({ category, data }) {
  const cfg = CATEGORY_CONFIG[category];
  if (!cfg || !data) return null;
  const Icon = cfg.icon;

  return (
    <div className="bg-zinc-900/50 border border-amber-500/25 rounded-sm p-5 space-y-4" data-testid={`cpi-hero-${category}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-sm flex items-center justify-center`} style={{ backgroundColor: `${cfg.color}15` }}>
            <Icon size={16} weight="bold" style={{ color: cfg.color }} />
          </div>
          <div>
            <h3 className="text-sm font-heading font-bold text-zinc-100">{cfg.label}</h3>
            <p className="text-[10px] text-zinc-500">{data.latest_month} {data.latest_year}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xl font-data font-bold text-zinc-50">{data.latest_value?.toFixed(1)}</p>
          <p className="text-[9px] text-zinc-600 uppercase tracking-wider">Index</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-3">
          <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Month-over-Month</p>
          <ChangeIndicator value={data.mom_change} />
        </div>
        <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-3">
          <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Year-over-Year</p>
          <ChangeIndicator value={data.yoy_change} />
        </div>
      </div>

      <div>
        <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-2">24-Month Trend</p>
        <CpiChart data={data.history} color={cfg.color} height={130} />
      </div>
    </div>
  );
}

function ComponentCard({ category, data }) {
  const cfg = CATEGORY_CONFIG[category];
  if (!cfg || !data) return null;
  const Icon = cfg.icon;

  return (
    <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-4 space-y-3 hover:border-zinc-700 transition-colors" data-testid={`cpi-component-${category}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={14} weight="bold" style={{ color: cfg.color }} />
          <span className="text-xs font-medium text-zinc-300">{cfg.label}</span>
        </div>
        <span className="text-sm font-data font-bold text-zinc-100">{data.latest_value?.toFixed(1)}</span>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-[9px] text-zinc-600 mb-0.5">MoM</p>
          <ChangeIndicator value={data.mom_change} />
        </div>
        <div className="text-right">
          <p className="text-[9px] text-zinc-600 mb-0.5">YoY</p>
          <ChangeIndicator value={data.yoy_change} />
        </div>
      </div>

      <CpiChart data={data.history} color={cfg.color} height={80} />

      <p className="text-[9px] text-zinc-600">{data.latest_month} {data.latest_year}</p>
    </div>
  );
}

export default function CpiReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState("overview");

  useEffect(() => {
    fetchCpi();
  }, []);

  const fetchCpi = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${API}/cpi`);
      setReport(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load CPI data");
    } finally {
      setLoading(false);
    }
  };

  const cats = report?.categories || {};
  const heroKeys = ["all_items", "food", "energy"];
  const componentKeys = ["core", "shelter", "transportation", "medical", "apparel", "new_vehicles", "used_vehicles"];

  const allAvailable = Object.entries(cats).filter(([k]) => CATEGORY_CONFIG[k]);

  return (
    <div className="p-5 space-y-5 max-w-[1600px]" data-testid="cpi-report">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Consumer Price Index</h1>
          <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1.5">
            <ChartLineUp size={12} />
            Live CPI data from the U.S. Bureau of Labor Statistics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="h-7 bg-zinc-800/50 p-0.5">
              <TabsTrigger value="overview" className="text-[10px] px-2.5 py-1 h-6 data-[state=active]:bg-zinc-700" data-testid="cpi-tab-overview">
                Overview
              </TabsTrigger>
              <TabsTrigger value="components" className="text-[10px] px-2.5 py-1 h-6 data-[state=active]:bg-zinc-700" data-testid="cpi-tab-components">
                All Components
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <button
            onClick={fetchCpi}
            disabled={loading}
            className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2 py-1 border border-zinc-800 rounded-sm"
            data-testid="cpi-refresh-btn"
          >
            <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-4 text-center" data-testid="cpi-error">
          <p className="text-xs text-red-400">{error}</p>
          <button onClick={fetchCpi} className="text-[10px] text-red-300 underline mt-1">Try Again</button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1,2,3].map(i => <Skeleton key={i} className="h-[320px] bg-zinc-900/50" />)}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-[180px] bg-zinc-900/50" />)}
          </div>
        </div>
      )}

      {/* Overview View */}
      {!loading && !error && view === "overview" && (
        <div className="space-y-5">
          {/* Hero Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="cpi-hero-grid">
            {heroKeys.map(key => cats[key] && (
              <HeroCard key={key} category={key} data={cats[key]} />
            ))}
          </div>

          {/* Quick Summary Bar */}
          {cats.all_items && (
            <div className="bg-zinc-900/30 border border-amber-500/15 rounded-sm p-4" data-testid="cpi-summary">
              <div className="flex items-center flex-wrap gap-6">
                <div>
                  <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Headline CPI (YoY)</p>
                  <p className={`text-lg font-data font-bold ${(cats.all_items.yoy_change || 0) > 3 ? "text-red-400" : (cats.all_items.yoy_change || 0) > 2 ? "text-amber-400" : "text-emerald-400"}`}>
                    {cats.all_items.yoy_change !== null ? `${cats.all_items.yoy_change > 0 ? "+" : ""}${cats.all_items.yoy_change.toFixed(2)}%` : "--"}
                  </p>
                </div>
                {cats.core && (
                  <div>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Core CPI (YoY)</p>
                    <p className={`text-lg font-data font-bold ${(cats.core.yoy_change || 0) > 3 ? "text-red-400" : (cats.core.yoy_change || 0) > 2 ? "text-amber-400" : "text-emerald-400"}`}>
                      {cats.core.yoy_change !== null ? `${cats.core.yoy_change > 0 ? "+" : ""}${cats.core.yoy_change.toFixed(2)}%` : "--"}
                    </p>
                  </div>
                )}
                {cats.food && (
                  <div>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Food (YoY)</p>
                    <ChangeIndicator value={cats.food.yoy_change} />
                  </div>
                )}
                {cats.energy && (
                  <div>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Energy (YoY)</p>
                    <ChangeIndicator value={cats.energy.yoy_change} />
                  </div>
                )}
                {cats.shelter && (
                  <div>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Shelter (YoY)</p>
                    <ChangeIndicator value={cats.shelter.yoy_change} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Component Grid */}
          <div>
            <h2 className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-3 font-medium">CPI Components</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cpi-components-grid">
              {componentKeys.map(key => cats[key] && (
                <ComponentCard key={key} category={key} data={cats[key]} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* All Components View */}
      {!loading && !error && view === "components" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cpi-all-components">
          {allAvailable.map(([key, data]) => (
            <HeroCard key={key} category={key} data={data} />
          ))}
        </div>
      )}

      {/* Footer */}
      {report && (
        <div className="flex items-center justify-between pt-2 border-t border-zinc-800/50" data-testid="cpi-footer">
          <p className="text-[10px] text-zinc-600">Source: {report.source}</p>
          <div className="flex items-center gap-1 text-[10px] text-zinc-600">
            <Clock size={10} />
            <span>Updated: {new Date(report.report_date).toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
}
